/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import { createApp } from 'vue';
import SequenceListener from './SequenceListener';
import VueGtag from 'vue-gtag-next';

// Bootstrap may need to be updated or changed based on your project's dependencies and structure
import './bootstrap';

const sequenceListener = new SequenceListener({
    debug: false,
    minLength: 8,
    ignoreInputs: true,
    allowedChars: false
});

sequenceListener.validateConfigs();

/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */

const app = createApp({
    methods: {
        ga0(params = {}) {
            this.$gtag.event('scan', params)
        },
        ga1(params = {}) {
            this.$gtag.event('area_clienti', params)
        },
        ga2(params = {}) {
            this.$gtag.event('search', params)
        },
        ga3(params = {}) {
            this.$gtag.event('search-click', params)
        },
        ga4(params = {}) {
            this.$gtag.event('menu', params)
        },
        ga5(params = {}) {
            this.$gtag.event('cart', params)
        },
        ga6(params = {}) {
            this.$gtag.event('store-select', params)
        },
        ga7(params = {}) {
            this.$gtag.event('billing', params)
        },
        ga8(params = {}) {
            this.$gtag.event('thankyou', params)
        },
        ga10(params = {}) {
            this.$gtag.event('cancel-order', params)
        }
    }
});

app.use(VueGtag, {
    property: {
        id: "G-8CVKH4B214"
    }
});

// Function to check for Vue directives safely
function checkForVueDirectives(element) {
    try {
        // Check for common Vue directives using safe selectors
        const vueSelectors = [
            '[v-on\\:click]',
            '[v-bind\\:]',
            '[v-model]',
            '[v-if]',
            '[v-for]',
            '[v-show]'
        ];
        
        for (const selector of vueSelectors) {
            if (element.querySelector(selector)) {
                return true;
            }
        }
        
        // Check innerHTML for Vue directives (less safe but more comprehensive)
        const htmlContent = element.innerHTML;
        return htmlContent.includes('v-on:') || 
               htmlContent.includes('v-bind:') ||
               htmlContent.includes('@click') ||
               htmlContent.includes('v-model') ||
               htmlContent.includes('v-if') ||
               htmlContent.includes('v-for');
    } catch (error) {
        console.warn('Error checking for Vue directives:', error);
        return true; // Default to mounting if we can't check safely
    }
}

// Wait for DOM to be ready and then mount if app element exists
function mountVueApp() {
    try {
        const appElement = document.getElementById('app');
        
        // Check if we're on a page that should have Vue (avoid auth pages)
        const isAuthPage = window.location.pathname.includes('/login') || 
                          window.location.pathname.includes('/register') ||
                          window.location.pathname.includes('/password');
        
        if (isAuthPage) {
            console.log('Vue app not mounted - auth page detected');
            return;
        }
        
        if (appElement) {
            // Check for Vue directives using safer methods
            const hasVueDirectives = checkForVueDirectives(appElement);
            
            if (hasVueDirectives) {
                app.mount('#app');
                console.log('Vue app mounted successfully');
            } else {
                console.log('Vue app not mounted - no Vue directives found');
            }
        } else {
            console.log('Vue app not mounted - #app element not found');
        }
    } catch (error) {
        console.error('Error mounting Vue app:', error);
    }
}

// Check if DOM is already ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
        // Add a small delay to ensure all content is rendered
        setTimeout(mountVueApp, 100);
    });
} else {
    // DOM is already ready, but wait a bit for content to load
    setTimeout(mountVueApp, 100);
}
